import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box,
} from '@mui/material';
import { useLocation ,useNavigate} from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../components/common/Loader';
import {
  getChapterDesiningStatus,
  getChapterEditingStatus,
  updateDataAsset,
  updateDepartmentVideo,
} from '../Servies/AdmiServices';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const formatTimestamp = (ts) => {
  if (!ts) return '-';
  try {
    const d = new Date(Number(ts));
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
};

export default function ChapterHistoryPage() {
  const query = useQuery();
  const navigate = useNavigate();
  const projectId = query.get('projectId');
  const projectChapterId = query.get('projectChapterId');
  const tabFromUrl = query.get('tab') || 'designing';
  const projectName = decodeURIComponent(query.get('projectName') || '');
  const chapterName = decodeURIComponent(query.get('chapterName') || '');
  const [isLoading, setIsLoading] = useState(false);

  const [pptList, setPptList] = useState([]);
  const [videoList, setVideoList] = useState([]);
  const [activeDesignTab, setActiveDesignTab] = useState('PPT');

  const [editingGroups, setEditingGroups] = useState({
    RECORDING: [],
    DUBBING: [],
    EDITING: [],
    ENGLISH_EDIT: [],
  });
  const [activeMainTab] = useState(tabFromUrl);
  const [activeEditGroup, setActiveEditGroup] = useState('ENGLISH_EDIT');

  const [openAssetDialog, setOpenAssetDialog] = useState(false);
  const [assetDialogData, setAssetDialogData] = useState({
    assetType: 'PPT',
    noOfContent: '',
    completedBy: '',
  });

  const [openDeptDialog, setOpenDeptDialog] = useState(false);
  const [deptDialogData, setDeptDialogData] = useState({
    workType: 'ENGLISH_EDIT',
    noOfContent: '',
    completedBy: '',
  });

  useEffect(() => {
    if (!projectId || !projectChapterId) {
      toast.error('projectId or projectChapterId missing in URL');
      return;
    }

    if (tabFromUrl === 'designing') fetchDesigning();
    else if (tabFromUrl === 'editing') fetchEditing();
  }, [projectId, projectChapterId, tabFromUrl]);

  const fetchDesigning = async () => {
    setIsLoading(true);
    try {
      const res = await getChapterDesiningStatus(projectId, projectChapterId);
      if (res?.data?.status === 'SUCCESS') {
        const resp = res.data.response || {};
        setPptList(resp.projectChapterPPTList || []);
        setVideoList(resp.projectChapterVideosList || []);
      } else {
        toast.error(res?.data?.message || 'Failed to fetch chapter designing history');
      }
    } catch {
      toast.error('Error fetching designing data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEditing = async () => {
    setIsLoading(true);
    try {
      const res = await getChapterEditingStatus(projectId, projectChapterId);
      if (res?.data?.status === 'SUCCESS') {
        const data = res.data.response?.data || {};
        setEditingGroups({
          RECORDING: data.RECORDING || [],
          DUBBING: data.DUBBING || [],
          EDITING: data.EDITING || [],
          ENGLISH_EDIT: data.ENGLISH_EDIT || [],
        });
      } else {
        toast.error(res?.data?.message || 'Failed to fetch editing history');
      }
    } catch {
      toast.error('Error fetching editing data');
    } finally {
      setIsLoading(false);
    }
  };

  const openUpdateAsset = (type) => {
    setAssetDialogData({ assetType: type, noOfContent: '', completedBy: '' });
    setOpenAssetDialog(true);
  };
  const handleBack = () => {
  navigate(`/dashboard/ProjectChapters?projectId=${projectId}`);
};

  const handleAssetSubmit = async () => {
    const payload = {
      assetType: assetDialogData.assetType,
      completedBy: assetDialogData.completedBy,
      noOfContent: Number(assetDialogData.noOfContent),
      projectChapterId: Number(projectChapterId),
    };
    setIsLoading(true);
    try {
      const res = await updateDataAsset(payload);
      if (res?.data?.status === 'SUCCESS') {
        toast.success(' Completion records updated Successfully ');
        setOpenAssetDialog(false);
        fetchDesigning();
      } else {
        toast.error(res?.data?.message || 'Failed to update asset');
      }
    } catch {
      toast.error('Error updating asset');
    } finally {
      setIsLoading(false);
    }
  };

  const openUpdateDept = (workType) => {
    setDeptDialogData({ workType, noOfContent: '', completedBy: '' });
    setOpenDeptDialog(true);
  };

  const handleDeptSubmit = async () => {
    const payload = {
      workType: deptDialogData.workType,
      completedBy: deptDialogData.completedBy,
      noOfContent: Number(deptDialogData.noOfContent),
      projectChapterId: Number(projectChapterId),
    };
    setIsLoading(true);
    try {
      const res = await updateDepartmentVideo(payload);
      if (res?.data?.status === 'SUCCESS') {
        toast.success('Completion records updated Successfully ');
        setOpenDeptDialog(false);
        fetchEditing();
      } else {
        toast.error(res?.data?.message || 'Failed to update department');
      }
    } catch {
      toast.error('Error updating department');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      {/* --- Back Button --- */}
            <Box sx={{ padding: "20px 40px" }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleBack}
                sx={{
                  borderColor: "#ccc",
                  "&:hover": { borderColor: "#888" },
                }}
              >
                ← Back
              </Button>
            </Box>

      {/* --- Page Header --- */}
      <Box sx={{ padding: "0 40px", mb: 2 }}>
        <Typography variant="h4" fontWeight={600}>
          Chapter History: {chapterName || 'N/A'}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
          Project: {projectName || 'N/A'}
        </Typography>
      </Box>

      {isLoading && <Loader />}

      <Container sx={{ mt: 3, mb: 4}}>
        {/* DESIGNING VIEW */}
        {activeMainTab === 'designing' && (
          <Card sx={{ p: 3, mb: 4 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              {['PPT', 'VIDEO'].map((tab) => (
                <Button
                  key={tab}
                  variant={activeDesignTab === tab ? 'contained' : 'outlined'}
                  onClick={() => setActiveDesignTab(tab)}
                >
                  {tab}
                </Button>
              ))}
            </Stack>

            {/*  PPT History */}
            {activeDesignTab === 'PPT' && (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    PPT HISTORY
                  </Typography>
                  <Button variant="contained" size="small" onClick={() => openUpdateAsset('PPT')}>
                    Update Completion Status
                  </Button>
                </Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>No. of PPT</TableCell>
                      <TableCell>Completed Date</TableCell>
                      <TableCell>Completed By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pptList.length > 0 ? (
                      pptList.map((r) => (
                        <TableRow key={r.id || Math.random()}>
                          <TableCell>{r.id}</TableCell>
                          <TableCell>{r.noOfPpt}</TableCell>
                          <TableCell>{formatTimestamp(r.completedDate)}</TableCell>
                          <TableCell>{r.completedBy || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}

            {activeDesignTab === 'VIDEO' && (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Video HISTORY
                  </Typography>
                  <Button variant="contained" size="small" onClick={() => openUpdateAsset('VIDEO')}>
                    Update Completion Status
                  </Button>
                </Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>No. of Video</TableCell>
                      <TableCell>Completed Date</TableCell>
                      <TableCell>Completed By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {videoList.length > 0 ? (
                      videoList.map((r) => (
                        <TableRow key={r.id || Math.random()}>
                          <TableCell>{r.id}</TableCell>
                          <TableCell>{r.noOfVideo}</TableCell>
                          <TableCell>{formatTimestamp(r.completedDate)}</TableCell>
                          <TableCell>{r.completedBy || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </Card>
        )}

        {/* EDITING VIEW */}
        {activeMainTab === 'editing' && (
          <Card sx={{ p: 3, mb: 4 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              {['RECORDING', 'DUBBING', 'EDITING', 'ENGLISH_EDIT'].map((g) => (
                <Button
                  key={g}
                  variant={activeEditGroup === g ? 'contained' : 'outlined'}
                  onClick={() => setActiveEditGroup(g)}
                >
                  {g}
                </Button>
              ))}
            </Stack>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {activeEditGroup} HISTORY
              </Typography>
              <Button variant="contained" size="small" onClick={() => openUpdateDept(activeEditGroup)}>
                Update Completion Status
              </Button>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>No. of Video</TableCell>
                  <TableCell>Completed Date</TableCell>
                  <TableCell>Completed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(editingGroups[activeEditGroup] || []).length > 0 ? (
                  editingGroups[activeEditGroup].map((r) => (
                    <TableRow key={r.id || Math.random()}>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.noOfVideo}</TableCell>
                      <TableCell>{formatTimestamp(r.completedDate)}</TableCell>
                      <TableCell>{r.completedBy || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Asset Update Dialog */}
        <Dialog open={openAssetDialog} onClose={() => setOpenAssetDialog(false)}>
          <DialogTitle>Update {assetDialogData.assetType} </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ width: 400, mt: 1 }}>
              <TextField
                label="No. of Content"
                type="number"
                value={assetDialogData.noOfContent}
                onChange={(e) => setAssetDialogData((s) => ({ ...s, noOfContent: e.target.value }))}
              />
              <TextField
                label="Completed By"
                value={assetDialogData.completedBy}
                onChange={(e) => setAssetDialogData((s) => ({ ...s, completedBy: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAssetDialog(false)}>Cancel</Button>
            <Button onClick={handleAssetSubmit} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dept Update Dialog */}
        <Dialog open={openDeptDialog} onClose={() => setOpenDeptDialog(false)}>
          <DialogTitle>Update {deptDialogData.workType}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ width: 400, mt: 1 }}>
              <TextField
                label="No. of Content"
                type="number"
                value={deptDialogData.noOfContent}
                onChange={(e) => setDeptDialogData((s) => ({ ...s, noOfContent: e.target.value }))}
              />
              <TextField
                label="Completed By"
                value={deptDialogData.completedBy}
                onChange={(e) => setDeptDialogData((s) => ({ ...s, completedBy: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeptDialog(false)}>Cancel</Button>
            <Button onClick={handleDeptSubmit} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
